import jsPDF from 'jspdf';

interface ROIPdfData {
  language: 'en' | 'sv';
  calculationType: 'revenue-lost' | 'costs-saved' | 'full-report';
  inputs: Record<string, number>;
  results: { monthlyValue: number; yearlyValue: number };
  promoterImpact?: { monthlyValue: number; yearlyValue: number; lostPromoters: number };
  costSavings?: { monthlyValue: number; yearlyValue: number };
  totalImpact?: { monthlyTotal: number; yearlyTotal: number };
  financialSummary?: { paybackMonths: number; yearOneROI: number };
  generatedAt: Date;
}

export const generateROIPdf = async (data: ROIPdfData): Promise<void> => {
  const { language, inputs, results, promoterImpact, costSavings, totalImpact, financialSummary, generatedAt } = data;
  const isEn = language === 'en';
  const isFullReport = data.calculationType === 'full-report';
  
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  // Brand colors (RGB)
  const brandOrange: [number, number, number] = [240, 91, 39];
  const brandPurple: [number, number, number] = [126, 102, 255];
  const greenColor: [number, number, number] = [34, 197, 94];
  const redColor: [number, number, number] = [239, 68, 68];
  const darkText: [number, number, number] = [30, 30, 30];
  const lightText: [number, number, number] = [100, 100, 100];
  const bgLight: [number, number, number] = [248, 250, 252];
  const bgCard: [number, number, number] = [255, 255, 255];
  
  const formatCurrency = (value: number) => 
    new Intl.NumberFormat(isEn ? 'en-US' : 'sv-SE', {
      style: 'currency',
      currency: isEn ? 'USD' : 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  
  // Page background
  pdf.setFillColor(...bgLight);
  pdf.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // Header gradient
  for (let i = 0; i < 50; i++) {
    const ratio = i / 50;
    const r = Math.round(brandPurple[0] + (brandOrange[0] - brandPurple[0]) * ratio);
    const g = Math.round(brandPurple[1] + (brandOrange[1] - brandPurple[1]) * ratio);
    const b = Math.round(brandPurple[2] + (brandOrange[2] - brandPurple[2]) * ratio);
    pdf.setFillColor(r, g, b);
    pdf.rect(0, i, pageWidth, 1.1, 'F');
  }
  
  // Logo
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(32);
  pdf.setFont('helvetica', 'bold');
  pdf.text('HostCall.ai', 20, 25);
  
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text(isEn ? 'AI-Powered Guest Communication' : 'AI-driven gästkommunikation', 20, 35);
  
  // Date box
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text(isEn ? 'ROI ANALYSIS' : 'ROI-ANALYS', pageWidth - 50, 22, { align: 'center' });
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text(generatedAt.toLocaleDateString(isEn ? 'en-US' : 'sv-SE', { 
    year: 'numeric', month: 'short', day: 'numeric' 
  }), pageWidth - 50, 32, { align: 'center' });
  
  let yPos = 60;
  
  // Total Impact Card
  if (isFullReport && totalImpact) {
    for (let i = 0; i < 55; i++) {
      const ratio = i / 55;
      const r = Math.round(brandOrange[0] - 20 * ratio);
      const g = Math.round(brandOrange[1] - 15 * ratio);
      const b = Math.round(brandOrange[2] - 10 * ratio);
      pdf.setFillColor(r, g, b);
      pdf.roundedRect(15, yPos + i, pageWidth - 30, 1.1, i === 0 ? 6 : 0, i === 0 ? 6 : 0, 'F');
    }
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(isEn ? 'TOTAL ANNUAL IMPACT' : 'TOTAL ÅRLIG PÅVERKAN', 25, yPos + 14);
    
    pdf.setFontSize(42);
    pdf.text(formatCurrency(totalImpact.yearlyTotal), pageWidth / 2, yPos + 38, { align: 'center' });
    
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${formatCurrency(totalImpact.monthlyTotal)} ${isEn ? 'per month' : 'per månad'}`, pageWidth / 2, yPos + 48, { align: 'center' });
    
    yPos += 65;
    
    // Three breakdown cards
    const cardWidth = (pageWidth - 40) / 3;
    const cardHeight = 40;
    
    // Revenue Lost
    pdf.setFillColor(...bgCard);
    pdf.roundedRect(15, yPos, cardWidth, cardHeight, 4, 4, 'F');
    pdf.setFillColor(...redColor);
    pdf.roundedRect(15, yPos, 4, cardHeight, 4, 0, 'F');
    
    pdf.setTextColor(...redColor);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text(isEn ? 'REVENUE LOST' : 'FÖRLORADE INTÄKTER', 25, yPos + 12);
    pdf.setTextColor(...darkText);
    pdf.setFontSize(16);
    pdf.text(formatCurrency(results.yearlyValue), 25, yPos + 28);
    
    // Promoter Impact
    const cardX2 = 20 + cardWidth;
    pdf.setFillColor(...bgCard);
    pdf.roundedRect(cardX2, yPos, cardWidth, cardHeight, 4, 4, 'F');
    pdf.setFillColor(...brandPurple);
    pdf.roundedRect(cardX2, yPos, 4, cardHeight, 4, 0, 'F');
    
    pdf.setTextColor(...brandPurple);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text(isEn ? 'PROMOTER IMPACT' : 'AMBASSADÖRSPÅVERKAN', cardX2 + 10, yPos + 12);
    pdf.setTextColor(...darkText);
    pdf.setFontSize(16);
    pdf.text(formatCurrency(promoterImpact?.yearlyValue || 0), cardX2 + 10, yPos + 28);
    
    // Cost Savings
    const cardX3 = 25 + cardWidth * 2;
    pdf.setFillColor(...bgCard);
    pdf.roundedRect(cardX3, yPos, cardWidth, cardHeight, 4, 4, 'F');
    pdf.setFillColor(...greenColor);
    pdf.roundedRect(cardX3, yPos, 4, cardHeight, 4, 0, 'F');
    
    pdf.setTextColor(...greenColor);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text(isEn ? 'COST SAVINGS' : 'KOSTNADSBESPARINGAR', cardX3 + 10, yPos + 12);
    pdf.setTextColor(...darkText);
    pdf.setFontSize(16);
    pdf.text(formatCurrency(costSavings?.yearlyValue || 0), cardX3 + 10, yPos + 28);
    
    yPos += cardHeight + 10;
    
    // Financial Summary Cards (Payback Period + Year 1 ROI)
    if (financialSummary) {
      const { paybackMonths, yearOneROI } = financialSummary;
      const metricsCardWidth = (pageWidth - 35) / 2;
      const metricsCardHeight = 32;
      
      // Payback Period Card
      pdf.setFillColor(...bgCard);
      pdf.roundedRect(15, yPos, metricsCardWidth, metricsCardHeight, 4, 4, 'F');
      pdf.setFillColor(...brandOrange);
      pdf.roundedRect(15, yPos, 4, metricsCardHeight, 4, 0, 'F');
      
      pdf.setTextColor(...brandOrange);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(isEn ? 'PAYBACK PERIOD' : 'ÅTERBETALNINGSTID', 25, yPos + 11);
      pdf.setTextColor(...darkText);
      pdf.setFontSize(18);
      pdf.text(`${paybackMonths.toFixed(1)} ${isEn ? 'months' : 'månader'}`, 25, yPos + 24);
      
      // Year 1 ROI Card
      const metricsCardX2 = 20 + metricsCardWidth;
      pdf.setFillColor(...bgCard);
      pdf.roundedRect(metricsCardX2, yPos, metricsCardWidth, metricsCardHeight, 4, 4, 'F');
      pdf.setFillColor(...greenColor);
      pdf.roundedRect(metricsCardX2, yPos, 4, metricsCardHeight, 4, 0, 'F');
      
      pdf.setTextColor(...greenColor);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(isEn ? 'YEAR 1 ROI' : 'ROI ÅR 1', metricsCardX2 + 10, yPos + 11);
      pdf.setTextColor(...darkText);
      pdf.setFontSize(18);
      pdf.text(`${yearOneROI.toFixed(0)}%`, metricsCardX2 + 10, yPos + 24);
      
      yPos += metricsCardHeight + 10;
    }
  }
  
  // Inputs section
  pdf.setFillColor(...bgCard);
  pdf.roundedRect(15, yPos, pageWidth - 30, 65, 4, 4, 'F');
  
  pdf.setTextColor(...brandPurple);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text(isEn ? 'Your Business Inputs' : 'Dina affärsuppgifter', 22, yPos + 12);
  
  yPos += 20;
  
  const inputLabels: Array<{ key: string; label: { en: string; sv: string }; format?: (v: number) => string }> = [
    { key: 'callsPerMonth', label: { en: 'Calls/Month', sv: 'Samtal/månad' } },
    { key: 'numberOfProperties', label: { en: 'Properties', sv: 'Fastigheter' } },
    { key: 'missedCallPercent', label: { en: 'Missed %', sv: 'Missade %' }, format: (v) => `${v}%` },
    { key: 'avgOrderValue', label: { en: 'Avg Order', sv: 'Snittorder' }, format: (v) => formatCurrency(v) },
    { key: 'staffHoursWeek', label: { en: 'Staff Hrs (Guest)', sv: 'Tim (gäst)' } },
    { key: 'hourlyStaffCost', label: { en: 'Hourly Cost', sv: 'Timkostnad' }, format: (v) => formatCurrency(v) }
  ];
  
  const colWidth = (pageWidth - 50) / 3;
  inputLabels.forEach((item, i) => {
    if (inputs[item.key] === undefined) return;
    const col = i % 3;
    const row = Math.floor(i / 3);
    const x = 22 + (col * colWidth);
    const y = yPos + (row * 14);
    
    pdf.setTextColor(...lightText);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.text(isEn ? item.label.en : item.label.sv, x, y);
    
    const value = inputs[item.key] || 0;
    pdf.setTextColor(...darkText);
    pdf.setFont('helvetica', 'bold');
    pdf.text(item.format ? item.format(value) : value.toLocaleString(), x + colWidth - 15, y, { align: 'right' });
  });
  
  yPos += 55;
  
  // CTA
  for (let i = 0; i < 35; i++) {
    const ratio = i / 35;
    const r = Math.round(brandPurple[0] + (brandOrange[0] - brandPurple[0]) * ratio);
    const g = Math.round(brandPurple[1] + (brandOrange[1] - brandPurple[1]) * ratio);
    const b = Math.round(brandPurple[2] + (brandOrange[2] - brandPurple[2]) * ratio);
    pdf.setFillColor(r, g, b);
    pdf.roundedRect(15, yPos + i, pageWidth - 30, 1.1, i === 0 ? 4 : 0, i === 0 ? 4 : 0, 'F');
  }
  
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text(isEn ? 'Ready to capture every call?' : 'Redo att fånga varje samtal?', 25, yPos + 15);
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text('hostcall.ai/demo', 25, yPos + 26);
  
  // Footer
  pdf.setFillColor(...brandPurple);
  pdf.rect(0, pageHeight - 15, pageWidth, 15, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(9);
  pdf.text('hostcall.ai', 15, pageHeight - 5);
  
  pdf.save(`HostCall-ROI-Report-${generatedAt.toISOString().split('T')[0]}.pdf`);
};

export default generateROIPdf;
